import React from 'react';
import { Link } from 'react-router-dom';
import { PublicHeader } from '../components/PublicHeader';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { PenTool, ShieldCheck, FileText, Hash, Lock, CheckCircle, Download, Search, ArrowRight } from 'lucide-react';

export const FormlioSignPage = () => {
  return (
    <div className="min-h-screen bg-white">
      <PublicHeader />

      {/* Hero */}
      <section className="py-20 px-6 bg-gradient-to-b from-indigo-50 to-white">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 bg-indigo-100 text-indigo-700 px-4 py-1.5 rounded-full text-sm font-medium mb-6">
            <PenTool className="w-4 h-4" />
            Formlio Sign
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 mb-4 leading-tight" data-testid="formlio-sign-title">
            Electronic Signatures,<br />Built Into Your Forms
          </h1>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto mb-8">
            Collect legally binding electronic signatures directly within your forms.
            Every signed document gets a unique document number, integrity hash, and downloadable PDF.
          </p>
          <div className="flex items-center justify-center gap-4">
            <Button asChild size="lg" className="bg-indigo-600 hover:bg-indigo-700 text-white px-8 h-12 text-base">
              <Link to="/signup">Get Started Free</Link>
            </Button>
            <Button asChild variant="outline" size="lg" className="px-8 h-12 text-base">
              <Link to="/verify">Verify a Document</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* How it Works */}
      <section className="py-20 px-6">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-2xl font-bold text-slate-900 text-center mb-12">How Formlio Sign Works</h2>
          <div className="grid md:grid-cols-4 gap-8">
            {[
              { step: '1', icon: FileText, title: 'Build Your Form', desc: 'Add a Signature field to any form in the builder.' },
              { step: '2', icon: PenTool, title: 'Collect Signatures', desc: 'Users review the document and sign electronically.' },
              { step: '3', icon: Hash, title: 'Document Number', desc: 'Each signed document gets a unique FL-SIGN number.' },
              { step: '4', icon: Download, title: 'Download PDF', desc: 'Signers receive a signed PDF with document metadata.' },
            ].map((item) => (
              <div key={item.step} className="text-center">
                <div className="w-14 h-14 bg-indigo-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <item.icon className="w-7 h-7 text-indigo-600" />
                </div>
                <div className="text-xs font-bold text-indigo-600 mb-1">STEP {item.step}</div>
                <h3 className="font-semibold text-slate-900 mb-1">{item.title}</h3>
                <p className="text-sm text-slate-600">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-20 px-6 bg-slate-50">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-2xl font-bold text-slate-900 text-center mb-12">Security & Verification</h2>
          <div className="grid md:grid-cols-3 gap-6">
            <Card>
              <CardContent className="pt-6">
                <ShieldCheck className="w-10 h-10 text-green-600 mb-4" />
                <h3 className="font-semibold text-slate-900 mb-2">Document Verification</h3>
                <p className="text-sm text-slate-600">
                  Every signed document can be verified at <strong>formlio.app/verify</strong> using its unique document number.
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <Lock className="w-10 h-10 text-indigo-600 mb-4" />
                <h3 className="font-semibold text-slate-900 mb-2">Integrity Hash</h3>
                <p className="text-sm text-slate-600">
                  SHA-256 document hashing ensures that any tampering is immediately detectable through the verification system.
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <CheckCircle className="w-10 h-10 text-purple-600 mb-4" />
                <h3 className="font-semibold text-slate-900 mb-2">Legal Compliance</h3>
                <p className="text-sm text-slate-600">
                  Electronic signatures comply with the E-SIGN Act, UETA, and similar laws. See our{' '}
                  <Link to="/consumer-consent" className="text-indigo-600 underline">Consumer Consent Disclosure</Link>.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Verify CTA */}
      <section className="py-16 px-6">
        <div className="max-w-3xl mx-auto text-center">
          <Search className="w-12 h-12 text-indigo-600 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-slate-900 mb-2">Verify a Document</h2>
          <p className="text-slate-600 mb-6">
            Have a document number? Check its authenticity and integrity status.
          </p>
          <Button asChild className="bg-indigo-600 hover:bg-indigo-700 text-white px-8 h-12">
            <Link to="/verify" className="flex items-center gap-2">
              Go to Verification
              <ArrowRight className="w-4 h-4" />
            </Link>
          </Button>
        </div>
      </section>

      {/* Footer links */}
      <section className="py-8 px-6 border-t border-slate-200">
        <div className="max-w-4xl mx-auto flex flex-wrap items-center justify-center gap-6 text-sm text-slate-600">
          <Link to="/consumer-consent" className="hover:text-indigo-600 transition-colors">Consumer Consent Disclosure</Link>
          <Link to="/security" className="hover:text-indigo-600 transition-colors">Security & Compliance</Link>
          <Link to="/privacy" className="hover:text-indigo-600 transition-colors">Privacy Policy</Link>
          <Link to="/terms" className="hover:text-indigo-600 transition-colors">Terms of Service</Link>
        </div>
        <p className="text-center text-xs text-indigo-600 font-medium mt-4">Powered by Formlio Sign</p>
      </section>
    </div>
  );
};

export default FormlioSignPage;
